import { domMax, LazyMotion } from "framer-motion";
import { lazy, Suspense, useEffect, useMemo } from "react";
import { BrowserRouter, Route, Routes, useLocation } from "react-router";
import { AdminRoute } from "./components/AdminRoute";
import { ClarityAnalytics } from "./components/ClarityAnalytics";
import { Layout } from "./components/Layout";
import { ProtectedRoute } from "./components/ProtectedRoute";
import { SimpleAnalytics } from "./components/SimpleAnalytics";
import { AuthProvider } from "./context/AuthProvider";
import { RulesProvider } from "./context/RulesProvider";
import type { Market } from "./domain/location/types";
import { MarketProvider, useT } from "./i18n";
import { getSeoHead } from "./lib/seoHead";
import { resolveClientMarket } from "./market/marketProfile";
import { LandingPage } from "./pages/LandingPage";
import { REFERENCE_DESIGN_PATHS } from "./pages/articles/referenceDesignsData";
import { UnitSystemProvider } from "./units/UnitSystemContext";

const AboutPage = lazy(() => import("./pages/AboutPage"));
const ContactPage = lazy(() => import("./pages/ContactPage"));
const PressPage = lazy(() => import("./pages/PressPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const NotFoundPage = lazy(() => import("./pages/NotFoundPage"));
const AuthPage = lazy(() =>
	import("./pages/AuthPage").then((m) => ({ default: m.AuthPage })),
);
const DashboardPage = lazy(() =>
	import("./pages/DashboardPage").then((m) => ({ default: m.DashboardPage })),
);
const ComparisonPage = lazy(() =>
	import("./pages/ComparisonPage").then((m) => ({ default: m.ComparisonPage })),
);
const BuilderPage = lazy(() =>
	import("./pages/BuilderPage").then((m) => ({ default: m.BuilderPage })),
);
const WizardPage = lazy(() =>
	import("./pages/WizardPage").then((m) => ({ default: m.WizardPage })),
);
const AdminPage = lazy(() =>
	import("./pages/AdminPage").then((m) => ({ default: m.AdminPage })),
);
const MaterialAuditPage = lazy(() =>
	import("./pages/MaterialAuditPage").then((m) => ({
		default: m.MaterialAuditPage,
	})),
);
const HeaterCalculatorPage = lazy(
	() => import("./pages/calculators/HeaterCalculatorPage"),
);
const SnowZoneCalculatorPage = lazy(
	() => import("./pages/calculators/SnowZoneCalculatorPage"),
);
const RunningCostCalculatorPage = lazy(
	() => import("./pages/calculators/RunningCostCalculatorPage"),
);
const SharePage = lazy(() =>
	import("./pages/SharePage").then((m) => ({ default: m.SharePage })),
);
const BastuKostnadGuide = lazy(
	() => import("./pages/articles/BastuKostnadGuide"),
);
const BastupanelGuide = lazy(() => import("./pages/articles/BastupanelGuide"));
const BastutunnaVsFyrkantigGuide = lazy(
	() => import("./pages/articles/BastutunnaVsFyrkantigGuide"),
);
const ByggaBastuGuide = lazy(() => import("./pages/articles/ByggaBatuGuide"));
const BygglovBastuGuide = lazy(
	() => import("./pages/articles/BygglovBastuGuide"),
);
const ElaggregatGuide = lazy(() => import("./pages/articles/ElaggregatGuide"));
const GuidesHub = lazy(() => import("./pages/articles/GuidesHub"));
const IntlGuides = lazy(() =>
	import("./pages/articles/IntlGuides").then((m) => ({
		default: m.IntlGuidesHub,
	})),
);
const BuildASaunaGuide = lazy(() =>
	import("./pages/articles/IntlGuides").then((m) => ({
		default: () => <m.IntlGuidePage path="/guide/build-a-sauna" />,
	})),
);
const SaunaCostGuide = lazy(() =>
	import("./pages/articles/IntlGuides").then((m) => ({
		default: () => <m.IntlGuidePage path="/guide/sauna-cost" />,
	})),
);
const SaunaPermitsGuide = lazy(() =>
	import("./pages/articles/IntlGuides").then((m) => ({
		default: () => <m.IntlGuidePage path="/guide/sauna-permits" />,
	})),
);
const SaunaHeaterSizingGuide = lazy(() =>
	import("./pages/articles/IntlGuides").then((m) => ({
		default: () => <m.IntlGuidePage path="/guide/sauna-heater-sizing" />,
	})),
);
const SaunaWoodTypesGuide = lazy(() =>
	import("./pages/articles/IntlGuides").then((m) => ({
		default: () => <m.IntlGuidePage path="/guide/sauna-wood-types" />,
	})),
);
const SaunaVentilationGuide = lazy(() =>
	import("./pages/articles/IntlGuides").then((m) => ({
		default: () => <m.IntlGuidePage path="/guide/sauna-ventilation" />,
	})),
);
const SnozonBastuGuide = lazy(
	() => import("./pages/articles/SnozonBastuGuide"),
);
const VedeldadBastuGuide = lazy(
	() => import("./pages/articles/VedeldadBastuGuide"),
);
const VentilationBastuGuide = lazy(
	() => import("./pages/articles/VentilationBastuGuide"),
);
const ReferenceDesignPage = lazy(() =>
	import("./pages/articles/VedeldadUtomhusbastu10m2").then((m) => ({
		default: m.ReferenceDesignPage,
	})),
);

function RouteLoadingFallback() {
	const t = useT();

	return (
		<div className="loading-screen" role="status" aria-live="polite">
			<div className="spinner" aria-hidden="true" />
			<p>{t.common_loading}</p>
		</div>
	);
}

function SeoHeadUpdater({ market }: { market: Market }) {
	const location = useLocation();

	useEffect(() => {
		const head = getSeoHead(location.pathname, market);
		document.documentElement.lang = head.lang;
		document.title = head.title;

		const setMeta = (selector: string, attr: string, value: string) => {
			let el = document.querySelector(selector) as HTMLMetaElement | null;
			if (!el) {
				el = document.createElement("meta");
				const [nameAttr, nameVal] =
					selector.match(/\[(.+?)="(.+?)"\]/)?.slice(1) ?? [];
				if (nameAttr && nameVal) el.setAttribute(nameAttr, nameVal);
				document.head.appendChild(el);
			}
			el.setAttribute(attr, value);
		};

		const setLink = (rel: string, href: string) => {
			let el = document.querySelector(
				`link[rel="${rel}"]`,
			) as HTMLLinkElement | null;
			if (!el) {
				el = document.createElement("link");
				el.setAttribute("rel", rel);
				document.head.appendChild(el);
			}
			el.setAttribute("href", href);
		};

		setMeta('meta[name="description"]', "content", head.description);
		setMeta('meta[property="og:title"]', "content", head.title);
		setMeta('meta[property="og:description"]', "content", head.description);
		setMeta('meta[property="og:url"]', "content", head.canonicalUrl);
		setMeta('meta[property="og:type"]', "content", head.ogType);
		setMeta('meta[property="og:image"]', "content", head.ogImage);
		setMeta('meta[property="og:image:secure_url"]', "content", head.ogImage);
		setMeta('meta[property="og:image:type"]', "content", head.ogImageType);
		setMeta('meta[property="og:image:width"]', "content", head.ogImageWidth);
		setMeta('meta[property="og:image:height"]', "content", head.ogImageHeight);
		setMeta('meta[property="og:image:alt"]', "content", head.ogImageAlt);
		setMeta('meta[property="og:site_name"]', "content", head.siteName);
		setMeta('meta[name="twitter:title"]', "content", head.title);
		setMeta(
			'meta[name="twitter:description"]',
			"content",
			head.twitterDescription,
		);
		setMeta('meta[name="twitter:image"]', "content", head.ogImage);
		setMeta('meta[name="twitter:image:alt"]', "content", head.ogImageAlt);
		setLink("canonical", head.canonicalUrl);

		document
			.querySelectorAll('link[rel="alternate"][hreflang]')
			.forEach((el) => {
				el.remove();
			});
		for (const alternate of head.alternates) {
			const el = document.createElement("link");
			el.setAttribute("rel", "alternate");
			el.setAttribute("hreflang", alternate.hreflang);
			el.setAttribute("href", alternate.href);
			document.head.appendChild(el);
		}
	}, [location.pathname, market]);

	return null;
}

function App() {
	const market = useMemo(
		() => resolveClientMarket(import.meta.env.VITE_MARKET_DEFAULT),
		[],
	);

	return (
		<LazyMotion features={domMax}>
			<MarketProvider market={market}>
				<UnitSystemProvider>
					<AuthProvider>
						<ClarityAnalytics />
						<SimpleAnalytics />
						<RulesProvider>
							<BrowserRouter>
							<SeoHeadUpdater market={market} />
							<Suspense fallback={<RouteLoadingFallback />}>
								<Routes>
									<Route element={<Layout />}>
										<Route path="/" element={<LandingPage />} />
										<Route path="/auth" element={<AuthPage />} />
										<Route path="/wizard" element={<WizardPage />} />
										<Route path="/dela/:config" element={<SharePage />} />
										<Route path="/privacy" element={<PrivacyPage />} />
										<Route path="/about" element={<AboutPage />} />
										<Route path="/contact" element={<ContactPage />} />
										<Route path="/press" element={<PressPage />} />
										<Route
											path="/guide/bygga-bastu"
											element={<ByggaBastuGuide />}
										/>
										<Route
											path="/guide/bastu-kostnad"
											element={<BastuKostnadGuide />}
										/>
										<Route
											path="/guide/bygglov-bastu"
											element={<BygglovBastuGuide />}
										/>
										<Route
											path="/guide/bastupanel"
											element={<BastupanelGuide />}
										/>
										<Route
											path="/guide/ventilation-bastu"
											element={<VentilationBastuGuide />}
										/>
										<Route
											path="/guide/elaggregat"
											element={<ElaggregatGuide />}
										/>
										<Route
											path="/guide/vedeldad-bastu"
											element={<VedeldadBastuGuide />}
										/>
										<Route
											path="/guide/snozon-bastu"
											element={<SnozonBastuGuide />}
										/>
										<Route
											path="/guide/bastutunna-vs-fyrkantig"
											element={<BastutunnaVsFyrkantigGuide />}
										/>
										{REFERENCE_DESIGN_PATHS.map((path) => (
											<Route
												key={path}
												path={path}
												element={<ReferenceDesignPage path={path} />}
											/>
										))}
										<Route
											path="/guide/build-a-sauna"
											element={<BuildASaunaGuide />}
										/>
										<Route
											path="/guide/sauna-cost"
											element={<SaunaCostGuide />}
										/>
										<Route
											path="/guide/sauna-permits"
											element={<SaunaPermitsGuide />}
										/>
										<Route
											path="/guide/sauna-heater-sizing"
											element={<SaunaHeaterSizingGuide />}
										/>
										<Route
											path="/guide/sauna-wood-types"
											element={<SaunaWoodTypesGuide />}
										/>
										<Route
											path="/guide/sauna-ventilation"
											element={<SaunaVentilationGuide />}
										/>
										<Route
											path="/guides"
											element={market === "se" ? <GuidesHub /> : <IntlGuides />}
										/>
										<Route
											path="/calculator/heater"
											element={<HeaterCalculatorPage />}
										/>
										<Route
											path="/calculator/snow-zone"
											element={<SnowZoneCalculatorPage />}
										/>
										<Route
											path="/calculator/running-cost"
											element={<RunningCostCalculatorPage />}
										/>
										<Route path="/builder/:type" element={<BuilderPage />} />
										<Route
											path="/dashboard"
											element={
												<ProtectedRoute>
													<DashboardPage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/dashboard/compare"
											element={
												<ProtectedRoute>
													<ComparisonPage />
												</ProtectedRoute>
											}
										/>
										<Route
											path="/admin"
											element={
												<AdminRoute>
													<AdminPage />
												</AdminRoute>
											}
										/>
										<Route
											path="/admin/material-audit"
											element={
												<AdminRoute>
													<MaterialAuditPage />
												</AdminRoute>
											}
										/>
										<Route path="*" element={<NotFoundPage />} />
									</Route>
								</Routes>
							</Suspense>
							</BrowserRouter>
						</RulesProvider>
					</AuthProvider>
				</UnitSystemProvider>
			</MarketProvider>
		</LazyMotion>
	);
}

export default App;
